import { create } from 'zustand';
import { config, type Language } from '../config/app';
import { en, tr, type Key } from './strings';

/*
    Translation, and the date formatting that goes with it.

    The chosen language is remembered per browser and defaults to whatever the
    deployment's config.json says, so a Turkish company's build opens in Turkish
    without every one of their staff changing a setting first.
*/

const KEY = 'webmail.lang.v1';

const TABLES: Record<Language, Record<Key, string>> = { en, tr };

function stored(): Language | null {
    try {
        const v = localStorage.getItem(KEY);
        return v === 'tr' || v === 'en' ? v : null;
    } catch {
        return null;
    }
}

interface LangState {
    lang: Language;
    /** True until the user picks one, so a later config change still applies. */
    auto: boolean;
    setLang: (lang: Language) => void;
}

export const useLang = create<LangState>((set) => {
    const saved = stored();
    return {
        lang: saved ?? config().defaultLanguage,
        auto: saved === null,
        setLang: (lang) => {
            try {
                localStorage.setItem(KEY, lang);
            } catch {
                /* private mode — it just won't be remembered */
            }
            document.documentElement.lang = lang;
            set({ lang, auto: false });
        },
    };
});

const BCP47: Record<Language, string> = { tr: 'tr-TR', en: 'en-GB' };

export const locale = (lang: Language): string => BCP47[lang];

function fill(text: string, vars?: Record<string, string | number>): string {
    if (!vars) return text;
    return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
        name in vars ? String(vars[name]) : whole,
    );
}

/** Non-reactive lookup, for code outside a component. */
export function translate(lang: Language, key: Key, vars?: Record<string, string | number>): string {
    return fill(TABLES[lang][key] ?? en[key] ?? key, vars);
}

/** The hook components use — re-renders them when the language changes. */
export function useT() {
    const lang = useLang((s) => s.lang);
    return {
        lang,
        t: (key: Key, vars?: Record<string, string | number>) => translate(lang, key, vars),
        /** Plural helper: English and Turkish both only need one/other here. */
        plural: (count: number, one: Key, other: Key) =>
            translate(lang, count === 1 ? one : other, { count: count.toLocaleString(locale(lang)) }),
        num: (n: number) => n.toLocaleString(locale(lang)),
    };
}

export function initLanguage() {
    const state = useLang.getState();
    // Nobody has chosen yet, so the deployment's default still wins.
    const lang = state.auto ? config().defaultLanguage : state.lang;
    document.documentElement.lang = lang;
    if (lang !== state.lang) useLang.setState({ lang });
}
